import { DialogRef } from '@angular/cdk/dialog';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { dispatch, select } from '@ngxs/store';
import { DYNAMIC_MODAL_REF_TOKEN } from '@shared/modules/dynamic-modal/dynamic-modal.const';
import { authLogOut } from '../auth/state/auth.actions';
import { AuthState } from '../auth/state/auth.state';
import { SESSION_CHECK_WARNING_MS, sessionCheckDeadlineAt } from './session-check.const';

/**
 * Countdown body of the session-ending warning modal.
 *
 * Ticks against the wall clock (not accumulated intervals), so throttled or
 * suspended background tabs cannot skew it. When new activity moves the deadline
 * forward, the modal dismisses itself; when the deadline passes, the user is
 * logged out with the session-expired flag.
 */
@Component({
  selector: 'app-session-check',
  templateUrl: './session-check.component.html',
  imports: [TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SessionCheckComponent implements OnInit, OnDestroy {
  readonly #modalRef = inject<DialogRef>(DYNAMIC_MODAL_REF_TOKEN);

  readonly #lastActivityAt = select(AuthState.lastActivityAt);

  readonly #logOut = dispatch(authLogOut);

  public readonly seconds = signal(0);

  #countDownTimer?: ReturnType<typeof setInterval>;

  public ngOnInit(): void {
    this.#onTick();
    this.#countDownTimer = setInterval(() => this.#onTick(), 1000);
  }

  public ngOnDestroy(): void {
    clearInterval(this.#countDownTimer);
  }

  #onTick(): void {
    const lastActivityAt = this.#lastActivityAt();
    if (!lastActivityAt) return;

    const remainingMs = sessionCheckDeadlineAt(lastActivityAt) - Date.now();

    if (remainingMs > SESSION_CHECK_WARNING_MS) {
      this.#modalRef.close();
      return;
    }

    this.seconds.set(Math.max(0, Math.ceil(remainingMs / 1000)));

    if (remainingMs <= 0) {
      clearInterval(this.#countDownTimer);
      this.#logOut(true);
    }
  }
}
